import React, { useMemo, useCallback, useState } from 'react';
import { Box, Popover } from '@mui/material';
import { StaticDatePicker as MuiStaticDatePicker } from '@mui/x-date-pickers';

import { getIsWednesday } from '@occc-app/lib/date';
import { ArrowDropDownIcon, Button } from '@occc/ui-design-system';

import {
  getMostRecentWednesday,
  getMinSelectableWednesday,
  buildWeekSelectorButtonLabel,
} from './week-selector.utils';

type WeekSelectorButtonProps = {
  value: Date;
  onChange: (date: Date) => void;
};

export const WeekSelectorButton = ({ value, onChange }: WeekSelectorButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const minDate = useMemo(() => getMinSelectableWednesday(), []);
  const maxDate = useMemo(() => getMostRecentWednesday(), []);

  const buttonLabel = useMemo(() => buildWeekSelectorButtonLabel(value), [value]);

  const handleAccept = useCallback(
    (acceptedDate: Date | null) => {
      if (!acceptedDate) {
        return;
      }

      onChange(acceptedDate);
      handleClose();
    },
    [onChange, handleClose],
  );

  return (
    <>
      <Button
        color='secondary'
        variant='contained'
        onClick={handleOpen}
        endIcon={
          <ArrowDropDownIcon
            sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}
          />
        }
      >
        {buttonLabel}
      </Button>

      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
            },
          },
        }}
      >
        <Box>
          <MuiStaticDatePicker
            defaultValue={value}
            disableFuture
            disableHighlightToday
            minDate={minDate}
            maxDate={maxDate}
            shouldDisableDate={(date) => !getIsWednesday(date)}
            onClose={handleClose}
            onAccept={handleAccept}
          />
        </Box>
      </Popover>
    </>
  );
};
